import { amplifyProxy } from '@/services/directApi'

export default {
  namespaced: true,
  state: {
    contractorsAll: [],
    bankless: [],
    emailless: [],
    onHold: [],
    totalCount: {
      all: 0,
      bankless: 0,
      emailless: 0,
      onHold: 0
    },
    pagination: {
      all: { offset: 0, limit: 10 },
      bankless: { offset: 0, limit: 10 },
      emailless: { offset: 0, limit: 10 },
      onHold: { offset: 0, limit: 10 }
    },
    loading: {
      all: false,
      bankless: false,
      emailless: false,
      onHold: false
    },
    error: {
      all: null,
      bankless: null,
      emailless: null,
      onHold: null
    },
    // Page cache to store previously fetched pages
    pageCache: {
      all: {},
      bankless: {},
      emailless: {},
      onHold: {}
    }
  },
  mutations: {
    SET_CONTRACTORS_ALL(state, data) {
      state.contractorsAll = data
    },
    SET_CONTRACTORS_BANKLESS_RECORDS(state, data) {
      state.bankless = data
    },
    SET_CONTRACTORS_EMAILLESS_RECORDS(state, data) {
      state.emailless = data
    },
    SET_CONTRACTORS_ON_HOLD(state, data) {
      state.onHold = data
    },
    SET_TOTAL_COUNT(state, { type, count }) {
      state.totalCount[type] = count
    },
    SET_PAGINATION(state, { type, offset, limit }) {
      state.pagination[type] = { offset, limit }
    },
    SET_LOADING(state, { type, value }) {
      state.loading[type] = value
    },
    SET_ERROR(state, { type, error }) {
      state.error[type] = error
    },
    SET_PAGE_CACHE(state, { type, key, data }) {
      state.pageCache[type][key] = data
    },
    CLEAR_PAGE_CACHE(state, type = null) {
      if (type) {
        state.pageCache[type] = {}
      } else {
        state.pageCache = {
          all: {},
          bankless: {},
          emailless: {},
          onHold: {}
        }
      }
    }
  },
  actions: {
    async getAllContractorsRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, contractorId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${contractorId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.all[cacheKey]) {
        const cachedData = state.pageCache.all[cacheKey]
        commit('SET_CONTRACTORS_ALL', cachedData.Data || cachedData.data || [])
        commit('SET_TOTAL_COUNT', { type: 'all', count: cachedData.Count || cachedData.count || 0 })
        commit('SET_PAGINATION', { type: 'all', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'all', value: true })
      commit('SET_ERROR', { type: 'all', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/contractors/all/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(contractorId && { contractorId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'all', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        
        commit('SET_CONTRACTORS_ALL', data)
        commit('SET_TOTAL_COUNT', { type: 'all', count })
        commit('SET_PAGINATION', { type: 'all', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'all', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'all', value: false })
      }
    },

    async getContractorsBanklessRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, contractorId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${contractorId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.bankless[cacheKey]) {
        const cachedData = state.pageCache.bankless[cacheKey]
        commit('SET_CONTRACTORS_BANKLESS_RECORDS', cachedData.Data || cachedData.data || [])
        commit('SET_TOTAL_COUNT', { type: 'bankless', count: cachedData.Count || cachedData.count || 0 })
        commit('SET_PAGINATION', { type: 'bankless', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'bankless', value: true })
      commit('SET_ERROR', { type: 'bankless', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/contractors/bankless/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(contractorId && { contractorId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'bankless', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        
        commit('SET_CONTRACTORS_BANKLESS_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'bankless', count })
        commit('SET_PAGINATION', { type: 'bankless', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'bankless', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'bankless', value: false })
      }
    },

    async getContractorsEmaillessRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, contractorId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${contractorId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.emailless[cacheKey]) {
        const cachedData = state.pageCache.emailless[cacheKey]
        commit('SET_CONTRACTORS_EMAILLESS_RECORDS', cachedData.Data || cachedData.data || [])
        commit('SET_TOTAL_COUNT', { type: 'emailless', count: cachedData.Count || cachedData.count || 0 })
        commit('SET_PAGINATION', { type: 'emailless', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'emailless', value: true })
      commit('SET_ERROR', { type: 'emailless', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/contractors/emailless/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(contractorId && { contractorId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'emailless', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        
        commit('SET_CONTRACTORS_EMAILLESS_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'emailless', count })
        commit('SET_PAGINATION', { type: 'emailless', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'emailless', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'emailless', value: false })
      }
    },

    async getContractorsOnHold({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, contractorId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${contractorId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.onHold[cacheKey]) {
        const cachedData = state.pageCache.onHold[cacheKey]
        commit('SET_CONTRACTORS_ON_HOLD', cachedData.Data || cachedData.data || [])
        commit('SET_TOTAL_COUNT', { type: 'onHold', count: cachedData.Count || cachedData.count || 0 })
        commit('SET_PAGINATION', { type: 'onHold', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'onHold', value: true })
      commit('SET_ERROR', { type: 'onHold', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/contractors/onHold/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(contractorId && { contractorId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'onHold', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        
        commit('SET_CONTRACTORS_ON_HOLD', data)
        commit('SET_TOTAL_COUNT', { type: 'onHold', count })
        commit('SET_PAGINATION', { type: 'onHold', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'onHold', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'onHold', value: false })
      }
    }
  }
}